import { Helmet } from 'react-helmet-async';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import Lottie from 'react-lottie';
import * as R from 'Router/routes';
import { Typography } from 'components';
import {
  usePSD2ProxyStatusQuery,
  usePSD2ProxySessionsDownloadMutation,
  usePSD2OfflineSessionsDownloadMutation,
  usePSD2OfflineStatusQuery,
  useClientQuery,
} from 'hooks/webapi';
import BankDocAnimation from 'assets/lottie/bank-doc-animation.json';
import H1 from 'components/H/H1';
import useError from 'hooks/useError';
import useNavigateSearch from 'hooks/useNavigateSearch';
import { useTypedNavigate } from 'hooks/useTypedNavigate';
import { useTranslation } from 'react-i18next';

const BankDocLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: BankDocAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const PSD2_ERRORS = [
  'ERROR',
  'NAME_SURNAME_MISMATCH',
  'INVALID_ACCOUNT_TYPE',
  'OUT_OF_RANGE',
  'WRONG_STATEMENT_AMOUNT',
] as const;

type PSD2ErrorType = (typeof PSD2_ERRORS)[number];

function PSD2Receiving() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateWithState } = useTypedNavigate();
  const location = useLocation();
  const params = useParams();
  const { showError } = useError();
  const navigateSearch = useNavigateSearch();
  const isOffline = location.pathname.includes(R.OFFLINE_PSD2_RECEIVING);
  // The offline link is opened without a login session, where GET /client is a 401
  const { data: client } = useClientQuery(!isOffline);

  const { mutateAsync: downloadHistory } =
    usePSD2ProxySessionsDownloadMutation();
  const { mutate: offlineDownloadHistory } =
    usePSD2OfflineSessionsDownloadMutation();

  const { data: statusData, isSuccess: isStatusSuccess } =
    usePSD2ProxyStatusQuery(!isOffline);
  const { data: offlineStatusData } = usePSD2OfflineStatusQuery(
    params.token,
    isOffline,
  );

  const downloadHistoryRequestRef = useRef(false);

  // Handle offline token redirect
  useEffect(() => {
    if (isOffline && !params.token) {
      const sessionToken = sessionStorage.getItem('offline-psd2-token');
      if (!sessionToken) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }
      navigateSearch(`${R.OFFLINE_PSD2_RECEIVING}/${sessionToken}`, {
        replace: true,
      });
    }
  }, [isOffline, params.token, navigateSearch, showError]);

  // Handle download history
  useEffect(() => {
    if (isOffline && !params.token) return;

    if (!downloadHistoryRequestRef.current) {
      if (isOffline && params.token) {
        downloadHistoryRequestRef.current = true;
        offlineDownloadHistory(params.token);
        return;
      }

      if (!isStatusSuccess) return;
      if (statusData?.typeOfService === 'PDF_PARSER') return;

      downloadHistoryRequestRef.current = true;
      downloadHistory();
    }
  }, [
    isStatusSuccess,
    statusData,
    isOffline,
    params.token,
    downloadHistory,
    offlineDownloadHistory,
  ]);

  // Handle status changes and navigation
  useEffect(() => {
    if (isOffline) {
      if (!offlineStatusData?.transactionHistory) return;

      if (offlineStatusData.transactionHistory === 'DONE') {
        const sessionToken = sessionStorage.getItem('offline-psd2-token');
        navigateSearch(
          `${R.OFFLINE_PSD2_VERIFIED}/${sessionToken}?status=${offlineStatusData.transactionHistory}`,
          { replace: true },
        );
        return;
      }

      if (
        PSD2_ERRORS.includes(
          String(offlineStatusData.transactionHistory) as PSD2ErrorType,
        )
      ) {
        navigateSearch(R.OFFLINE_PSD2_ERROR);
      }
      return;
    }

    if (!statusData?.transactionHistory) return;

    if (statusData.transactionHistory === 'DONE') {
      navigate(R.PSD2_VERIFIED);
      return;
    }

    if (
      PSD2_ERRORS.includes(
        String(statusData.transactionHistory) as PSD2ErrorType,
      ) &&
      client?.status !== undefined
    ) {
      if (client.status !== 'IDENTIFIED') {
        navigate(R.WELCOME_BACK);
        return;
      }
      navigateWithState(R.PSD2_ERROR, {
        from: 'PSD2Receiving',
        error: {
          typeOfService: statusData.typeOfService,
          status: statusData.status,
          errorReason: statusData.errorReason,
          transactionHistory: statusData.transactionHistory,
        },
      });
    }
  }, [
    statusData,
    offlineStatusData,
    isOffline,
    navigate,
    navigateSearch,
    client?.status,
  ]);

  return (
    <>
      <Helmet>
        <title>{t('psd2_receiving:html_title')}</title>
      </Helmet>

      <div className="h-full flex flex-col justify-center items-center lg:items-start">
        <H1 dataTest="psd2Receiving-title">{t('psd2_receiving:page_title')}</H1>

        <Typography
          dataTest="psd2Receiving-description"
          className="text-center lg:text-left"
        >
          {t('psd2_receiving:description')}
        </Typography>

        <div
          className="my-10 mx-auto lg:mx-0 w-fit"
          data-test="psd2Receiving-lottie-wrapper"
        >
          <Lottie options={BankDocLottieOptions} />
        </div>
      </div>
    </>
  );
}

export default PSD2Receiving;
